import { computed, onMounted, onBeforeUnmount, reactive, ref, watch, provide } from "vue";

export type Axis = "both" | "x" | "y";
export type Bounds =
  | null
  | "parent"
  | { left?: number; top?: number; right?: number; bottom?: number };

export interface DraggableProps {
  modelValue?: { x: number; y: number } | null;
  defaultPosition?: { x: number; y: number } | "center";
  axis?: Axis;
  bounds?: Bounds;
  grid?: [number, number] | null;
  handle?: boolean | string; // class name or selector
  cancel?: string | null; // selector to cancel drag
  draggingDisabled?: boolean;
  arrowKeyMovements?: boolean;
  enableUserSelectHack?: boolean;
  enableSizingToChild?: boolean;
  zIndexWhileDragging?: number | string | null;
  teleportTo?: string;
  backdrop?: boolean;
  backgroundLocked?: boolean;
}

type Emits = {
  (e: "update:modelValue", value: { x: number; y: number }): void;
  (e: "dragstart", value: { x: number; y: number }, evt: PointerEvent): void;
  (e: "drag", value: { x: number; y: number }, evt: PointerEvent): void;
  (e: "dragend", value: { x: number; y: number }, evt: PointerEvent): void;
};

export type Size = "small" | "medium" | "large" | "xl";

export function useDraggable(props: DraggableProps, emit: Emits) {
  const rootDraggable = ref<HTMLElement | null>(null);
  const rootDraggableChild = ref<HTMLElement | null>(null);
  const dragging = ref(false);
  const position = reactive({ x: 0, y: 0 });
  const startState = reactive({ startX: 0, startY: 0, origX: 0, origY: 0 });

  const isControlled = computed(() => props.modelValue !== null);
  const useAbsolute = computed(() => props.bounds === "parent");
  const grabbing = computed(() => dragging.value && !props.draggingDisabled);

  // --- Helpers ---
  function matches(el: Element, selector: string) {
    return (el as HTMLElement).matches?.(selector);
  }
  function findCancelTarget(target: Element): boolean {
    if (!props.cancel) return false;
    let el: Element | null = target;
    while (el && el !== rootDraggable.value) {
      if (matches(el, props.cancel)) return true;
      el = el.parentElement;
    }
    return false;
  }

  // If handle is a bare class name, normalize to ".class"
  function normalizeHandleSelector(h?: string | null): string | null {
    if (!h) return null;
    // If it starts with . # [ or letter+specials, assume selector
    if (h.startsWith(".") || h.startsWith("#") || h.startsWith("[") || /[ >:+~]/.test(h)) {
      return h;
    }
    // Treat as class name
    return `.${h}`;
  }

  function applyAxis(next: { x: number; y: number }) {
    if (props.axis === "x") return { x: next.x, y: position.y };
    if (props.axis === "y") return { x: position.x, y: next.y };
    return next;
  }
  function applyGrid(next: { x: number; y: number }) {
    if (!props.grid) return next;
    const [gx, gy] = props.grid;
    return { x: Math.round(next.x / gx) * gx, y: Math.round(next.y / gy) * gy };
  }
  function getBoundsRect(): { left: number; top: number; right: number; bottom: number } | null {
    if (props.bounds === "parent") {
      const el = rootDraggable.value as HTMLElement | null;
      const parent = el?.offsetParent as HTMLElement | null;
      if (!el || !parent) return null;
      const right = Math.max(parent.clientWidth - el.offsetWidth, 0);
      const bottom = Math.max(parent.clientHeight - el.offsetHeight, 0);
      return { left: 0, top: 0, right, bottom };
    }
    if (!props.bounds) return null;
    const { left = 0, top = 0, right = Infinity, bottom = Infinity } = props.bounds;
    return { left, top, right, bottom };
  }
  function applyBounds(next: { x: number; y: number }) {
    const b = getBoundsRect();
    if (!b) return next;
    return {
      x: Math.min(Math.max(next.x, b.left), b.right),
      y: Math.min(Math.max(next.y, b.top), b.bottom),
    };
  }
  function updatePosition(
    next: { x: number; y: number },
    evt: PointerEvent | KeyboardEvent | null,
    phase: "drag" | "start" | "end",
  ) {
    const withAxis = applyAxis(next);
    const withGrid = applyGrid(withAxis);
    const clamped = applyBounds(withGrid);

    if (isControlled.value) emit("update:modelValue", { x: clamped.x, y: clamped.y });
    else {
      position.x = clamped.x;
      position.y = clamped.y;
    }

    if (evt && phase === "drag") emit("drag", { x: clamped.x, y: clamped.y }, evt as PointerEvent);
    if (evt && phase === "start")
      emit("dragstart", { x: clamped.x, y: clamped.y }, evt as PointerEvent);
    if (evt && phase === "end")
      emit("dragend", { x: clamped.x, y: clamped.y }, evt as PointerEvent);
  }

  // --- Event handlers ---
  function onPointerDown(e: PointerEvent) {
    if (props.draggingDisabled) return;
    const target = e.target as Element;

    // NB. so we can interact with any items inside the draggable element
    const ignoreSelector = "button, a, input, textarea, select, [role='button'], .modal-buttons";

    if (target instanceof HTMLElement && target.closest(ignoreSelector)) {
      return;
    }

    // Only allow drag from handle if provided (class name or selector)
    const handleSelector = normalizeHandleSelector(props.handle as string | null);

    if (handleSelector) {
      let el: Element | null = target;
      let matched = false;
      while (el && el !== rootDraggable.value) {
        if (matches(el, handleSelector)) {
          matched = true;
          break;
        }
        el = el.parentElement;
      }
      if (!matched) return; // clicked outside handle → do not start drag
    }

    // Cancel selector short-circuit
    if (findCancelTarget(target)) return;

    dragging.value = true;
    rootDraggable.value?.setPointerCapture?.(e.pointerId);

    startState.startX = e.clientX;
    startState.startY = e.clientY;
    startState.origX = position.x;
    startState.origY = position.y;

    if (props.enableUserSelectHack) document.documentElement.classList.add("dw-noselect");
    updatePosition({ x: position.x, y: position.y }, e, "start");
  }
  function onPointerMove(e: PointerEvent) {
    if (!dragging.value) return;
    const dx = e.clientX - startState.startX;
    const dy = e.clientY - startState.startY;
    updatePosition({ x: startState.origX + dx, y: startState.origY + dy }, e, "drag");
  }

  function onPointerUp(e: PointerEvent) {
    if (!dragging.value) return;
    dragging.value = false;
    document.documentElement.classList.remove("dw-noselect");
    updatePosition({ x: position.x, y: position.y }, e, "end");
  }

  function onKeyDown(e: KeyboardEvent) {
    if (props.draggingDisabled || props.arrowKeyMovements) return;
    const step = props.grid ? Math.max(props.grid[0], props.grid[1]) : 1;
    let handled = true;
    if (e.key === "ArrowLeft") updatePosition({ x: position.x - step, y: position.y }, e, "drag");
    else if (e.key === "ArrowRight")
      updatePosition({ x: position.x + step, y: position.y }, e, "drag");
    else if (e.key === "ArrowUp")
      updatePosition({ x: position.x, y: position.y - step }, e, "drag");
    else if (e.key === "ArrowDown")
      updatePosition({ x: position.x, y: position.y + step }, e, "drag");
    else handled = false;
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  // TODO. rename to reCalculateDraggableModalSize
  provide("reCalculateDraggableModalSize", () => {
    // call when modal content being sized & content changing
    doSomethingOnResize();
  });

  async function setInitial() {
    await Promise.resolve();
    const el = rootDraggable.value;
    const parent = el?.offsetParent as HTMLElement | null;

    const handleSelector = normalizeHandleSelector(props.handle as string | null);
    if (handleSelector) {
      const customHandleElement = el.querySelector(handleSelector);
      customHandleElement?.classList.add("dw--draggable-handle");
    }

    if (props.bounds === "parent" && el && parent) {
      const pw = parent.clientWidth;
      const ph = parent.clientHeight;
      const ew = el.offsetWidth;
      const eh = el.offsetHeight;

      const centerX = Math.max(0, (pw - ew) / 2);
      const centerY = Math.max(0, (ph - eh) / 2);

      // Only use defaultPosition if it's an object; ignore "center"
      const given =
        props.modelValue ??
        (typeof props.defaultPosition === "object" ? props.defaultPosition : null);

      let x = given?.x ?? centerX;
      let y = given?.y ?? centerY;

      if (props.axis === "x") y = position.y;
      if (props.axis === "y") x = position.x;

      const maxX = Math.max(pw - ew, 0);
      const maxY = Math.max(ph - eh, 0);
      position.x = Math.min(Math.max(x, 0), maxX);
      position.y = Math.min(Math.max(y, 0), maxY);
      return;
    }

    // Non-parent bounds: center -> (0,0), object -> that position, otherwise (0,0)
    if (props.defaultPosition === "center") {
      position.x = 0;
      position.y = 0;
    } else {
      const posLike =
        props.modelValue ??
        (typeof props.defaultPosition === "object" ? props.defaultPosition : undefined);

      // Destructure with defaults to avoid reading x/y of undefined
      const { x = 0, y = 0 } = (posLike as { x?: number; y?: number } | undefined) ?? {};
      position.x = x;
      position.y = y;
    }
  }
  // Resize (window, parent, self)
  let roParent: ResizeObserver | null = null;
  let roSelf: ResizeObserver | null = null;
  let rafPending = false;

  function clampFromResize() {
    if (props.bounds !== "parent") return;
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      const bounded = applyBounds({ x: position.x, y: position.y });
      if (bounded.x !== position.x || bounded.y !== position.y) {
        if (isControlled.value) emit("update:modelValue", bounded);
        else {
          position.x = bounded.x;
          position.y = bounded.y;
        }
      }
    });
  }

  function setupResizeObservers() {
    if (props.bounds !== "parent") return;
    const el = rootDraggable.value as HTMLElement | null;
    const parent = el?.offsetParent as HTMLElement | null;
    if (!el || !parent || typeof ResizeObserver === "undefined") return;
    roParent = new ResizeObserver(clampFromResize);
    roSelf = new ResizeObserver(clampFromResize);
    roParent.observe(parent);
    roSelf.observe(el);
  }

  let roSelfSize: ResizeObserver | null = null;

  function setupModalResizeObservers() {
    const el = rootDraggable.value as HTMLElement | null;
    const parent = el?.offsetParent as HTMLElement | null;

    if (!el || !parent || typeof ResizeObserver === "undefined") return;

    roSelfSize?.disconnect(); // avoid double-observing if setup runs twice

    roSelfSize = new ResizeObserver(doSomethingOnResize);
    roSelfSize.observe(el);
  }

  function doSomethingOnResize() {
    // If setInitial() changes layout, do it outside the observer tick
    requestAnimationFrame(async () => {
      await setInitial();
      getChildSize();
    });
  }

  function teardownResizeObservers() {
    roParent?.disconnect();
    roSelf?.disconnect();
    roSelfSize?.disconnect();

    roParent = null;
    roSelf = null;
    roSelfSize = null;
  }

  // keep controlled in sync
  watch(
    () => props.modelValue,
    (val) => {
      if (!val) return;
      if (val.x !== position.x || val.y !== position.y) {
        position.x = val.x;
        position.y = val.y;
      }
    },
    { deep: true },
  );

  watch(rootDraggableChild, (val) => {
    if (val) {
      getChildSize();
    }
  });

  //  NB. sometime we use the same modal but load different sized components
  const rootChildSize = ref<Size | null>(null);

  function getChildSize(): Size | null {
    const el =
      rootDraggableChild.value ?? (rootDraggable.value.firstElementChild as HTMLElement | null);
    if (!el) return null;

    const width = el.clientWidth;
    let size: Size;

    if (width <= 320) size = "small";
    else if (width <= 700) size = "medium";
    else if (width <= 1000) size = "large";
    else if (width <= 1200) size = "xl";
    rootChildSize.value = size;

    return size;
  }

  // reconfigure when bounds toggles
  watch(
    () => props.bounds,
    () => {
      teardownResizeObservers();
      setupResizeObservers();
      clampFromResize();
    },
  );

  onMounted(() => {
    setInitial();
    const el = rootDraggable.value!;
    if (!el) return;
    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("resize", clampFromResize, { passive: true });
    (el as any).style.touchAction = "none";
    document.body.classList.add("dw--draggable-item--present");
    setupResizeObservers();
    setupModalResizeObservers();
    if (props.enableUserSelectHack) {
      // ensure cleanup in case component unmounts mid-drag
      document.documentElement.classList.remove("dw-noselect");
    }
    if (rootDraggable.value) {
      rootDraggableChild.value = rootDraggable.value.firstElementChild as HTMLElement | null;
    }
  });

  onBeforeUnmount(() => {
    const el = rootDraggable.value;
    el?.removeEventListener("pointerdown", onPointerDown);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("resize", clampFromResize);
    teardownResizeObservers();
    document.documentElement.classList.remove("dw-noselect");
    document.body.classList.remove("dw--draggable-item--present");
  });

  const styleObj = computed(() => {
    if (useAbsolute.value) {
      return {
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: grabbing.value ? props.zIndexWhileDragging ?? undefined : undefined,
      } as Partial<CSSStyleDeclaration>;
    }
    return {
      transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
      zIndex: grabbing.value ? props.zIndexWhileDragging ?? undefined : undefined,
    } as Partial<CSSStyleDeclaration>;
  });

  const target = document?.querySelector(`${props?.teleportTo}`);
  const isEmpty = !target || target.childElementCount === 0;

  watch(
    () => props.backdrop,
    (enabled) => {
      if (enabled) {
        document.body.classList.add("no-scroll");
      } else {
        document.body.classList.remove("no-scroll");
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    // clean up in case backdrop was still true
    // TODO only if its the last one.
    if (isEmpty) document.body.classList.remove("no-scroll");
  });

  return { rootDraggable, styleObj, grabbing, useAbsolute, onKeyDown, rootChildSize };
}
